/**
 * Glucose data filtering utilities
 * Removes anomalous CGM readings that are physiologically impossible or represent sensor errors
 */

import {
  DEVICE_DATA_WINDOW_DAYS,
  DEVICE_WARMUP_PERIOD_SECONDS,
  DISLODGEMENT_DETECTION,
  DLS_GLUCOSE_SENTINEL_MAX_RAW,
  GLUCOSE_VALID_RANGE,
} from '../common'
import { Device } from '../types'

export interface GlucoseReading {
  timestamp: Date | number
  glucose: number
}

/**
 * A time range (Unix seconds) hidden from charts because the sensor is
 * suspected to be dislodged. `end` is null when no recovery was seen, meaning
 * data is hidden through the end of the series.
 *
 * This is the single source of truth shared by the data filter
 * (`filterByDevicesDislodgement`) and the chart "suspected detachment" overlay
 * + debug logging, so what is hidden and what is annotated can never diverge.
 */
export interface DislodgementRange {
  deviceId: number
  start: number
  end: number | null
  reason: string
}

export interface FilterConfig {
  // Physiological limits in mmol/L
  minValidGlucose?: number
  maxValidGlucose?: number
  // Rate of change limits (mmol/L per minute)
  maxRateOfChange?: number
  // Minimum time interval between readings (ms)
  minInterval?: number
}

const DEFAULT_CONFIG: Required<FilterConfig> = {
  minValidGlucose: GLUCOSE_VALID_RANGE.min, // ~54 mg/dL - below this is likely sensor error (single source of truth from common.ts)
  maxValidGlucose: GLUCOSE_VALID_RANGE.max, // ~540 mg/dL - above this is likely sensor error (single source of truth from common.ts)
  maxRateOfChange: 0.5, // 0.5 mmol/L per minute (~9 mg/dL/min) - extremely rapid change threshold
  minInterval: 30000, // 30 seconds - readings closer than this may be duplicates
}

/**
 * Checks if a glucose value is within physiologically valid range
 */
export function isValidGlucoseValue(
  glucose: number | null | undefined,
  config: FilterConfig = {},
): boolean {
  if (glucose == null || !Number.isFinite(glucose)) {
    return false
  }

  const { minValidGlucose, maxValidGlucose } = { ...DEFAULT_CONFIG, ...config }

  return glucose >= minValidGlucose && glucose <= maxValidGlucose
}

/**
 * Calculates the rate of change between two readings (mmol/L per minute)
 */
function calculateRateOfChange(reading1: GlucoseReading, reading2: GlucoseReading): number {
  const time1 =
    reading1.timestamp instanceof Date ? reading1.timestamp.getTime() : reading1.timestamp
  const time2 =
    reading2.timestamp instanceof Date ? reading2.timestamp.getTime() : reading2.timestamp

  const timeDiffMinutes = Math.abs(time2 - time1) / (60 * 1000)

  if (timeDiffMinutes === 0) return 0

  const glucoseDiff = Math.abs(reading2.glucose - reading1.glucose)
  return glucoseDiff / timeDiffMinutes
}

/**
 * Filters glucose readings to remove anomalies
 * Returns readings with null glucose values where anomalies were detected
 * This preserves timestamp spacing while creating visual gaps in charts
 */
export function filterGlucoseAnomalies<T extends GlucoseReading>(
  readings: T[],
  config: FilterConfig = {},
): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  const fullConfig = { ...DEFAULT_CONFIG, ...config }
  const filtered: T[] = []

  for (let i = 0; i < readings.length; i++) {
    const reading = readings[i]

    // Check basic validity
    if (!isValidGlucoseValue(reading.glucose, fullConfig)) {
      // Keep the reading but null out the glucose value to create a gap
      filtered.push({ ...reading, glucose: null } as any)
      continue
    }

    // Check rate of change against previous valid reading
    if (filtered.length > 0) {
      const lastValidReading = [...filtered].reverse().find((r) => r.glucose != null)

      if (lastValidReading) {
        const rateOfChange = calculateRateOfChange(lastValidReading, reading)

        if (rateOfChange > fullConfig.maxRateOfChange) {
          // Anomalous rate of change - null out this reading
          filtered.push({ ...reading, glucose: null } as any)
          continue
        }
      }
    }

    // Check for duplicate timestamps (too close together)
    if (filtered.length > 0) {
      const prevTimestamp = filtered[filtered.length - 1].timestamp
      const prevTime = prevTimestamp instanceof Date ? prevTimestamp.getTime() : prevTimestamp
      const currTimestamp = reading.timestamp
      const currTime = currTimestamp instanceof Date ? currTimestamp.getTime() : currTimestamp

      if (Math.abs(currTime - prevTime) < fullConfig.minInterval) {
        // Skip this duplicate reading entirely
        continue
      }
    }

    // Valid reading
    filtered.push(reading)
  }

  return filtered
}

/**
 * Removes readings with null glucose values (for statistics calculations)
 * Use this after filterGlucoseAnomalies for computing averages, TIR, etc.
 */
export function removeNullReadings<T extends GlucoseReading>(readings: T[]): T[] {
  return readings.filter((r) => r.glucose != null && Number.isFinite(r.glucose))
}

/**
 * Gets count of filtered anomalies
 */
export function getAnomalyCount(
  originalReadings: GlucoseReading[],
  filteredReadings: GlucoseReading[],
): number {
  const originalValid = originalReadings.filter((r) => r.glucose != null).length
  const filteredValid = filteredReadings.filter((r) => r.glucose != null).length
  return originalValid - filteredValid
}

/**
 * Calculates the warm-up end time for a device
 * @param deviceStartTime Unix timestamp in seconds when device was activated
 * @returns Unix timestamp in seconds when warm-up period ends
 */
export function getWarmupEndTime(deviceStartTime: number): number {
  return deviceStartTime + DEVICE_WARMUP_PERIOD_SECONDS
}

/**
 * Checks if a timestamp is within the device warm-up period
 * @param timestamp Unix timestamp in seconds (or milliseconds - will be auto-detected)
 * @param deviceStartTime Unix timestamp in seconds when device was activated
 * @returns true if the timestamp is within the warm-up period
 */
export function isInWarmupPeriod(timestamp: number, deviceStartTime: number): boolean {
  if (!deviceStartTime || deviceStartTime <= 0) {
    return false
  }

  // Auto-detect if timestamp is in milliseconds (> year 2001 in seconds)
  const normalizedTimestamp = timestamp > 1000000000000 ? timestamp / 1000 : timestamp

  const warmupEnd = getWarmupEndTime(deviceStartTime)
  return normalizedTimestamp >= deviceStartTime && normalizedTimestamp < warmupEnd
}

/**
 * Filters out readings that fall within the device warm-up period
 * During warm-up (1 hour after device activation), glucose readings are not clinically valid
 * @param readings Array of glucose readings with timestamp (in seconds or ms)
 * @param deviceStartTime Unix timestamp in seconds when device was activated
 * @returns Filtered array with warm-up readings removed
 */
export function filterByDeviceWarmup<T extends { timestamp: number | Date }>(
  readings: T[],
  deviceStartTime: number | null | undefined,
): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  // If no device start time, return all readings (can't determine warm-up period)
  if (!deviceStartTime || deviceStartTime <= 0) {
    return readings
  }

  const warmupEnd = getWarmupEndTime(deviceStartTime)

  return readings.filter((reading) => {
    const timestamp =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp

    // Auto-detect if timestamp is in milliseconds
    const normalizedTimestamp = timestamp > 1000000000000 ? timestamp / 1000 : timestamp

    // Only keep readings AFTER the warm-up period ends
    // This also excludes pre-activation readings (from previous sensor sessions)
    return normalizedTimestamp >= warmupEnd
  })
}

/**
 * Filters out readings from multiple devices, excluding warm-up periods for each
 * @param readings Array of glucose readings with device and timestamp
 * @param devices Map of device ID to device object with start_time
 * @returns Filtered array with warm-up readings removed
 */
export function filterByDevicesWarmup<
  T extends { timestamp: number | Date; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  // Convert array to map if needed
  const deviceMap = Array.isArray(devices) ? new Map(devices.map((d) => [d.id, d])) : devices

  if (deviceMap.size === 0) {
    return readings
  }

  return readings.filter((reading) => {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    const device = deviceMap.get(deviceId)

    if (!device?.start_time) {
      // Device not found or has no start_time — discard the reading
      // (unknown device data is not trustworthy)
      return false
    }

    const timestamp =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp

    // Auto-detect if timestamp is in milliseconds
    const normalizedTimestamp = timestamp > 1000000000000 ? timestamp / 1000 : timestamp

    // Only keep readings AFTER the warm-up period ends
    // This also excludes pre-activation readings (from previous sensor sessions)
    const warmupEnd = getWarmupEndTime(device.start_time)
    return normalizedTimestamp >= warmupEnd
  })
}

/**
 * Filters readings to only include data within the 14-day window from device activation
 * @param readings Array of readings with timestamp
 * @param deviceStartTime Unix timestamp in seconds when device was activated
 * @returns Filtered array with only readings within 14-day window
 */
export function filterBy14DayWindow<T extends { timestamp: number | Date }>(
  readings: T[],
  deviceStartTime: number | null | undefined,
): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  // If no device start time, return all readings (can't determine window)
  if (!deviceStartTime || deviceStartTime <= 0) {
    return readings
  }

  const windowEndTime = deviceStartTime + DEVICE_DATA_WINDOW_DAYS * 24 * 60 * 60 // 14 days in seconds

  return readings.filter((reading) => {
    const timestamp =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp

    // Auto-detect if timestamp is in milliseconds
    const normalizedTimestamp = timestamp > 1000000000000 ? timestamp / 1000 : timestamp

    // Keep readings within the 14-day window (from device start to window end)
    return normalizedTimestamp >= deviceStartTime && normalizedTimestamp <= windowEndTime
  })
}

/**
 * Filters readings for multiple devices, applying 14-day window for each device
 * @param readings Array of readings with device and timestamp
 * @param devices Map or array of devices with start_time
 * @returns Filtered array with only readings within each device's 14-day window
 */
export function filterByDevices14DayWindow<
  T extends { timestamp: number | Date; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  // Convert array to map if needed
  const deviceMap = Array.isArray(devices) ? new Map(devices.map((d) => [d.id, d])) : devices

  if (deviceMap.size === 0) {
    return readings
  }

  return readings.filter((reading) => {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    const device = deviceMap.get(deviceId)

    if (!device?.start_time) {
      // No start time available, keep the reading
      return true
    }

    const windowEndTime = device.start_time + DEVICE_DATA_WINDOW_DAYS * 24 * 60 * 60

    const timestamp =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp

    // Auto-detect if timestamp is in milliseconds
    const normalizedTimestamp = timestamp > 1000000000000 ? timestamp / 1000 : timestamp

    // Keep readings within the 14-day window
    return normalizedTimestamp >= device.start_time && normalizedTimestamp <= windowEndTime
  })
}

/**
 * Detects the timestamp when device dislodgement occurred using a sliding window.
 *
 * A detached sensor typically produces readings clipped to the minimum, but noise
 * can cause occasional non-minimum readings that break a strict consecutive check.
 * This uses a window-based approach: if ≥90% of readings in any window of 60 are
 * at-minimum, dislodgement is triggered at the first minimum reading in that window.
 *
 * @param readings Array of readings sorted by timestamp ASC
 * @returns Unix timestamp (seconds) when dislodgement started, or null if no dislodgement detected
 */
/**
 * The stored-glucose value at or below which a reading is this device's
 * firmware "no valid reading" placeholder rather than a measurement.
 *
 * WHY THIS IS PER-DEVICE. `detectDislodgementTimestamp` used to compare a raw
 * STORED value against `GLUCOSE_VALID_RANGE.min + tolerance` (2.7). But 2.2 is
 * a DISPLAY bound, and a DLS QR calibration is `a·x² + b·x + c`, so a stored
 * value can never fall below its intercept `c`. 809 of 813 prod DLS devices
 * have `c = 3.0`, so the comparison was arithmetically impossible for 99.5% of
 * the fleet and the detector had never once fired (vetpo#2).
 *
 * The bound returned here is the one ada-core's Metrics/sentinel.ts uses to set
 * `is_sentinel`: the calibrated value at the placeholder raw bound. For
 * `a, b >= 0` the map is monotonically increasing over `x >= 0`, so
 * `calibrated <= a·r² + b·r + c  ⟺  raw <= r`. Where that argument does not
 * hold — no calibration, unparseable, negative coefficients, or a linear BIOE
 * model whose `b` is a subtrahend (`nA*k - b`) and not an intercept — this
 * fails OPEN and returns the previous global constant, marking nothing extra.
 *
 * ⚠️ NO SLACK is added on top. Measured 2026-08-26 across 90 days of prod, the
 * stored distribution from 3.0 to 4.0 mmol/L is flat and continuous (~5-7k
 * readings per 0.05 bucket over 158-314 devices) — there is no gap between a
 * pinned sensor and a genuine severe low, and raw 2.2 mmol/L (dangerous, real)
 * stores as 3.2246 on the common calibration. Widening this bound to make the
 * detector fire more would hide real hypoglycemia, which it must never do.
 */
export function sensorFloorThreshold(device?: Device | null): number {
  const failOpen = GLUCOSE_VALID_RANGE.min + DISLODGEMENT_DETECTION.minimumGlucoseTolerance
  const raw = device?.calibration
  if (!raw) return failOpen

  let cal: Record<string, unknown>
  try {
    cal = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>)
  } catch {
    return failOpen
  }
  if (!cal || typeof cal !== 'object') return failOpen

  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const a = num(cal.a)
  const b = num(cal.b)
  const c = num(cal.c)

  // Quadratic QR model only. A BIOE record carries `k`/`b` and no `a`/`c`, and
  // its `b` is subtracted, so it must never be read as an intercept.
  if (a === null || c === null || b === null) return failOpen
  if (a < 0 || b < 0) return failOpen

  const r = DLS_GLUCOSE_SENTINEL_MAX_RAW
  return a * r * r + b * r + c
}

export function detectDislodgementTimestamp<
  T extends { timestamp: number | Date; glucose: number },
>(readings: T[], floorThreshold?: number): number | null {
  const windowSize = DISLODGEMENT_DETECTION.windowSize
  if (!readings || readings.length < windowSize) {
    return null
  }

  // Callers that know the device pass its own floor (sensorFloorThreshold).
  // The default keeps the previous global bound for the device-less callers,
  // which is fail-open: it marks nothing on a DLS sensor.
  const minGlucoseThreshold =
    floorThreshold ?? GLUCOSE_VALID_RANGE.min + DISLODGEMENT_DETECTION.minimumGlucoseTolerance
  const minRequired = Math.ceil(windowSize * DISLODGEMENT_DETECTION.windowMinFraction)

  const isAtMin = (r: T) => r.glucose != null && r.glucose <= minGlucoseThreshold

  // Initialize count for the first window
  let minCount = 0
  for (let i = 0; i < windowSize; i++) {
    if (isAtMin(readings[i])) minCount++
  }

  // Slide the window across all readings
  for (let end = windowSize; end <= readings.length; end++) {
    if (minCount >= minRequired) {
      // Find the first at-minimum reading in this window — that's the dislodgement start
      const windowStart = end - windowSize
      for (let j = windowStart; j < end; j++) {
        if (isAtMin(readings[j])) {
          const stamp = readings[j].timestamp
          const raw = stamp instanceof Date ? stamp.getTime() / 1000 : stamp
          return raw > 1000000000000 ? raw / 1000 : raw
        }
      }
    }

    // Slide: add next reading, remove oldest
    if (end < readings.length) {
      if (isAtMin(readings[end])) minCount++
      if (isAtMin(readings[end - windowSize])) minCount--
    }
  }

  return null
}

/**
 * Detects the first timestamp after `dislodgementTimestamp` where glucose
 * recovers to a healthy level for `recoveryReadingsThreshold` consecutive
 * readings. Returns null if no such recovery exists in the data.
 *
 * Without this, `filterByDislodgement` would discard every reading after
 * the dislodgement trigger forever — even readings from later days when
 * the sensor is clearly producing normal values again (UFL trial, May 2026).
 *
 * @param readings Array of readings sorted by timestamp ASC
 * @param dislodgementTimestamp Unix seconds when dislodgement started
 * @returns Unix seconds of the first reading in the recovery run, or null
 */
export function detectRecoveryAfterDislodgement<
  T extends { timestamp: number | Date; glucose: number },
>(readings: T[], dislodgementTimestamp: number): number | null {
  const recoveryThreshold = DISLODGEMENT_DETECTION.recoveryGlucoseThreshold
  const recoveryNeeded = DISLODGEMENT_DETECTION.recoveryReadingsThreshold

  let consecutiveNormal = 0
  let recoveryStart: number | null = null

  for (const reading of readings) {
    const raw =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp
    const ts = raw > 1000000000000 ? raw / 1000 : raw

    if (ts < dislodgementTimestamp) continue

    if (reading.glucose != null && reading.glucose >= recoveryThreshold) {
      if (consecutiveNormal === 0) recoveryStart = ts
      consecutiveNormal++
      if (consecutiveNormal >= recoveryNeeded) {
        return recoveryStart
      }
    } else {
      consecutiveNormal = 0
      recoveryStart = null
    }
  }

  return null
}

/**
 * Filters out readings during a detected dislodgement window
 *
 * If the data recovers (glucose returns to normal for several consecutive
 * readings), only the low-glucose window itself is hidden — the post-recovery
 * data is preserved. If recovery is never seen in the data, everything from
 * the dislodgement onwards is hidden (sensor presumed permanently dislodged).
 *
 * @param readings Array of readings sorted by timestamp ASC
 * @returns Filtered array with the dislodgement window removed
 */
export function filterByDislodgement<T extends { timestamp: number | Date; glucose: number }>(
  readings: T[],
): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  const dislodgementTimestamp = detectDislodgementTimestamp(readings)

  if (dislodgementTimestamp === null) {
    return readings
  }

  const recoveryTimestamp = detectRecoveryAfterDislodgement(readings, dislodgementTimestamp)

  return readings.filter((reading) => {
    const raw =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp
    const ts = raw > 1000000000000 ? raw / 1000 : raw

    if (ts < dislodgementTimestamp) return true
    if (recoveryTimestamp !== null && ts >= recoveryTimestamp) return true
    return false
  })
}

/**
 * Detects dislodgement for multiple devices and returns a map of device ID to dislodgement timestamp
 * @param readings Array of readings with device info
 * @param devices Map or array of devices
 * @returns Map of device ID to dislodgement timestamp (null if no dislodgement)
 */
export function detectDevicesDislodgement<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): Map<number, number | null> {
  const result = new Map<number, number | null>()

  // Convert array to map if needed
  const deviceMap = Array.isArray(devices) ? new Map(devices.map((d) => [d.id, d])) : devices

  // Group readings by device
  const readingsByDevice = new Map<number, T[]>()
  for (const reading of readings) {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    if (!readingsByDevice.has(deviceId)) {
      readingsByDevice.set(deviceId, [])
    }
    readingsByDevice.get(deviceId)!.push(reading)
  }

  // Detect dislodgement for each device
  Array.from(readingsByDevice.entries()).forEach(([deviceId, deviceReadings]) => {
    // Sort readings by timestamp
    const sortedReadings = [...deviceReadings].sort((a, b) => {
      const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() / 1000 : a.timestamp
      const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() / 1000 : b.timestamp
      return timestampA - timestampB
    })

    const dislodgementTimestamp = detectDislodgementTimestamp(
      sortedReadings,
      sensorFloorThreshold(deviceMap.get(deviceId)),
    )
    result.set(deviceId, dislodgementTimestamp)
  })

  return result
}

/**
 * Computes the hidden time ranges (per device) caused by suspected sensor
 * dislodgement. Each range runs from the dislodgement start up to its recovery
 * timestamp (`end`), or to the end of the series (`end: null`) when the sensor
 * never recovers.
 *
 * This is the SINGLE SOURCE OF TRUTH for "what is hidden and why." Both the
 * data filter (`filterByDevicesDislodgement`) and the chart overlay / debug
 * logging derive from it, so the hidden data and the annotated range can never
 * disagree.
 *
 * @param readings Array of readings with device info
 * @param devices Map or array of devices
 * @returns One DislodgementRange per device that hit dislodgement (empty if none)
 */
export function getDislodgementRanges<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): DislodgementRange[] {
  if (!readings || readings.length === 0) {
    return []
  }

  const dislodgementMap = detectDevicesDislodgement(readings, devices)

  // Group readings by device for per-device recovery computation
  const readingsByDevice = new Map<number, T[]>()
  for (const reading of readings) {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    if (!readingsByDevice.has(deviceId)) readingsByDevice.set(deviceId, [])
    readingsByDevice.get(deviceId)!.push(reading)
  }

  const pct = Math.round(DISLODGEMENT_DETECTION.windowMinFraction * 100)
  const reason = `>=${pct}% of a ${DISLODGEMENT_DETECTION.windowSize}-reading window at sensor-minimum glucose`

  const ranges: DislodgementRange[] = []
  Array.from(dislodgementMap.entries()).forEach(([deviceId, dislodgementTimestamp]) => {
    if (dislodgementTimestamp === null) return
    const deviceReadings = readingsByDevice.get(deviceId) || []
    const sorted = [...deviceReadings].sort((a, b) => {
      const ta = a.timestamp instanceof Date ? a.timestamp.getTime() / 1000 : a.timestamp
      const tb = b.timestamp instanceof Date ? b.timestamp.getTime() / 1000 : b.timestamp
      return ta - tb
    })
    const recovery = detectRecoveryAfterDislodgement(sorted, dislodgementTimestamp)
    ranges.push({ deviceId, start: dislodgementTimestamp, end: recovery, reason })
  })

  return ranges
}

/**
 * Drops readings that fall inside any hidden dislodgement range for their device.
 * A reading is kept if it is before the range start, or at/after the recovery end.
 */
function applyDislodgementRanges<T extends { timestamp: number | Date; device: number | Device }>(
  readings: T[],
  ranges: DislodgementRange[],
): T[] {
  if (ranges.length === 0) return readings

  const rangeByDevice = new Map<number, DislodgementRange>()
  for (const range of ranges) rangeByDevice.set(range.deviceId, range)

  return readings.filter((reading) => {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    const range = rangeByDevice.get(deviceId)
    if (!range) return true

    const raw =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp
    const ts = raw > 1000000000000 ? raw / 1000 : raw

    if (ts < range.start) return true
    if (range.end !== null && ts >= range.end) return true
    return false
  })
}

/**
 * Filters readings for multiple devices, removing post-dislodgement data for each
 * @param readings Array of readings with device info
 * @param devices Map or array of devices
 * @returns Filtered array with post-dislodgement readings removed for each device
 */
export function filterByDevicesDislodgement<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  const ranges = getDislodgementRanges(readings, devices)
  return applyDislodgementRanges(readings, ranges)
}

/**
 * Combines all device data filters: warm-up and dislodgement detection
 * This is the recommended function to use for comprehensive filtering
 *
 * Note: 14-day window filter has been DISABLED. While CGM sensors typically have
 * a 14-day lifespan, some devices produce valid data beyond 14 days. The dislodgement
 * detection handles cases where the sensor fails or is disconnected, which is a more
 * accurate way to determine end of valid data than a hard time cutoff.
 *
 * @param readings Array of readings with timestamp and glucose
 * @param deviceStartTime Unix timestamp in seconds when device was activated
 * @returns Filtered array with all filters applied
 */
export function filterDeviceData<T extends { timestamp: number | Date; glucose: number }>(
  readings: T[],
  deviceStartTime: number | null | undefined,
): T[] {
  if (!readings || readings.length === 0) {
    return []
  }

  // Apply filters in sequence:
  // 1. Remove warm-up period data
  let filtered = filterByDeviceWarmup(readings, deviceStartTime)

  // 2. DISABLED: 14-day window filter
  // Some devices produce valid data beyond 14 days. Dislodgement detection
  // is a more accurate way to determine end of valid data.
  // filtered = filterBy14DayWindow(filtered, deviceStartTime)

  // 3. Sort by timestamp for dislodgement detection
  const sorted = [...filtered].sort((a, b) => {
    const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() : a.timestamp
    const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() : b.timestamp
    return timestampA - timestampB
  })

  // 4. Remove post-dislodgement data
  filtered = filterByDislodgement(sorted)

  return filtered
}

/**
 * Single-device variant of {@link filterDevicesDataWithRanges} that preserves
 * the lenient warm-up behavior of {@link filterDeviceData}: when the device has
 * no `start_time`, its readings are kept rather than discarded. Used by the main
 * trend charts, where a single selected device may not carry a start_time in
 * every data path. Returns the hidden dislodgement ranges so the chart overlay
 * and the hidden data stay in lockstep.
 *
 * @param readings Array of readings with device, timestamp, and glucose
 * @param deviceStartTime Unix seconds when the device was activated (may be null)
 * @param devices Map or array containing the single device (for range grouping)
 * @returns `{ filtered, ranges }` — filtered readings and the hidden ranges
 */
export function filterDeviceDataWithRanges<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(
  readings: T[],
  deviceStartTime: number | null | undefined,
  devices: Map<number, Device> | Device[],
): { filtered: T[]; ranges: DislodgementRange[] } {
  if (!readings || readings.length === 0) {
    return { filtered: [], ranges: [] }
  }

  // 1. Lenient warm-up removal (keeps readings when start_time is unknown)
  const warmupFiltered = filterByDeviceWarmup(readings, deviceStartTime)

  // 2. Sort by timestamp for dislodgement detection (id tie-break keeps
  // equal-timestamp duplicates deterministic regardless of fetch direction)
  const sorted = [...warmupFiltered].sort((a, b) => {
    const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() : a.timestamp
    const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() : b.timestamp
    return (
      timestampA - timestampB ||
      Number((a as { id?: number | string }).id ?? 0) -
        Number((b as { id?: number | string }).id ?? 0)
    )
  })

  // 3. Compute hidden ranges once, then apply them (single source of truth)
  const ranges = getDislodgementRanges(sorted, devices)
  return { filtered: applyDislodgementRanges(sorted, ranges), ranges }
}

/**
 * Remove overlapping device data by keeping only readings from the most
 * recently activated device at each point in time.
 *
 * When a pet has sequential CGM sensors, a new sensor's activation implicitly
 * supersedes the old one. Any readings from the old device after the new
 * device's start_time are discarded to prevent duplicate/conflicting values.
 */
function filterOverlappingDevices<T extends { timestamp: number | Date; device: number | Device }>(
  readings: T[],
  deviceMap: Map<number, Device>,
): T[] {
  if (deviceMap.size <= 1) return readings

  // Sort devices by start_time ascending
  const sortedDevices = Array.from(deviceMap.values())
    .filter((d) => d.start_time && d.start_time > 0)
    .sort((a, b) => (a.start_time || 0) - (b.start_time || 0))

  if (sortedDevices.length <= 1) return readings

  return readings.filter((reading) => {
    const deviceId = typeof reading.device === 'number' ? reading.device : reading.device?.id
    if (!deviceId) return true

    const readingTs =
      reading.timestamp instanceof Date ? reading.timestamp.getTime() / 1000 : reading.timestamp

    // Find the active device at this timestamp:
    // the device with the latest start_time that's <= readingTs
    let activeDevice: Device | null = null
    for (const device of sortedDevices) {
      if ((device.start_time || 0) <= readingTs) {
        activeDevice = device
      } else {
        break
      }
    }

    if (!activeDevice) return false // Reading is before any device activation — discard
    return deviceId === activeDevice.id
  })
}

/**
 * Combines all device data filters for multiple devices: warm-up and dislodgement detection
 *
 * Note: 14-day window filter has been DISABLED. While CGM sensors typically have
 * a 14-day lifespan, some devices produce valid data beyond 14 days. The dislodgement
 * detection handles cases where the sensor fails or is disconnected, which is a more
 * accurate way to determine end of valid data than a hard time cutoff.
 *
 * @param readings Array of readings with device, timestamp, and glucose
 * @param devices Map or array of devices
 * @returns Filtered array with all filters applied for each device
 */
export function filterDevicesData<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(readings: T[], devices: Map<number, Device> | Device[]): T[] {
  return filterDevicesDataWithRanges(readings, devices).filtered
}

/**
 * Same pipeline as `filterDevicesData`, but also returns the hidden dislodgement
 * ranges so callers (charts) can overlay a "suspected sensor detachment" band
 * and emit debug logging. The ranges are computed on the exact warm-up +
 * overlap-filtered, sorted readings the dislodgement filter operates on, so the
 * band and the hidden data always agree.
 *
 * @returns `{ filtered, ranges }` — filtered readings and the hidden ranges
 */
export function filterDevicesDataWithRanges<
  T extends { timestamp: number | Date; glucose: number; device: number | Device },
>(
  readings: T[],
  devices: Map<number, Device> | Device[],
): { filtered: T[]; ranges: DislodgementRange[] } {
  if (!readings || readings.length === 0) {
    return { filtered: [], ranges: [] }
  }

  // Convert array to map if needed
  const deviceMap = Array.isArray(devices) ? new Map(devices.map((d) => [d.id, d])) : devices

  if (deviceMap.size === 0) {
    return { filtered: readings, ranges: [] }
  }

  // Apply filters in sequence:
  // 1. Remove warm-up period data
  let filtered = filterByDevicesWarmup(readings, deviceMap)

  // 2. DISABLED: 14-day window filter
  // Some devices produce valid data beyond 14 days. Dislodgement detection
  // is a more accurate way to determine end of valid data.
  // filtered = filterByDevices14DayWindow(filtered, deviceMap)

  // 3. Remove overlapping device data (newest device wins)
  filtered = filterOverlappingDevices(filtered, deviceMap)

  // 4. Sort by timestamp for dislodgement detection (id tie-break keeps
  // equal-timestamp duplicates deterministic regardless of fetch direction)
  const sorted = [...filtered].sort((a, b) => {
    const timestampA = a.timestamp instanceof Date ? a.timestamp.getTime() : a.timestamp
    const timestampB = b.timestamp instanceof Date ? b.timestamp.getTime() : b.timestamp
    return (
      timestampA - timestampB ||
      Number((a as { id?: number | string }).id ?? 0) -
        Number((b as { id?: number | string }).id ?? 0)
    )
  })

  // 5. Compute hidden ranges once, then apply them (single source of truth)
  const ranges = getDislodgementRanges(sorted, deviceMap)
  return { filtered: applyDislodgementRanges(sorted, ranges), ranges }
}
